import { admin, companyId } from './userSelectors';
import { createSelector } from '@/redux/lib/createSelector.js';
import { getDeep } from '@shared/utils/getDeep.js';
import { logStackTrace } from '@shared/utils/logStackTrace.js';

const APP_DEV_MODE_COMPANY_IDS = ['4Fd4dvLbSX4H', 'GixMk7yF6ht8']; // Logiwa, Centerpoint Connect

export const pathname = (state) => state?.router?.location?.pathname;
export const filters = (state) => state?.ui?.filters;
export const mobile = (state) => state?.ui?.mobile === true;
const params = (state) => state?.ui?.params;
export const param = createSelector(
  'param',
  params,
  (_, key) => key,
  (params, key) => params?.[key]
);
export const prefs = (state) => state?.ui?.prefs;
export const canUseAppDevMode = createSelector('canUseAppDevMode', [admin, companyId], (isAdmin, activeCompanyId) => {
  return isAdmin || APP_DEV_MODE_COMPANY_IDS.includes(activeCompanyId);
});
export const appDevModeEnabled = createSelector('appDevModeEnabled', [prefs, canUseAppDevMode], (items, hasAccess) => {
  if (!hasAccess) {
    return false;
  }

  return items?.appDevMode === true;
});

export const workflowCollapsedStepIds = createSelector('workflowCollapsedStepIds', [prefs], (prefs) => {
  if (!prefs) return '';
  return Object.keys(prefs)
    .filter((k) => k.startsWith('step-collapsed-') && prefs[k])
    .map((k) => k.replace('step-collapsed-', ''))
    .sort()
    .join(',');
});

export const pref = createSelector(
  'pref',
  [prefs, (_, key, __) => key, (_, __, callback) => callback], //
  (items, key, callback) => {
    if (typeof key === 'object') {
      console.log('found object!', key);
      logStackTrace(100);
    }
    const value = key ? getDeep(key, items) : items;
    return callback ? callback(value) : value;
  }
);
export const search = createSelector('search', [(state) => state.ui.search, (_, key) => key], (search, key) => search?.[key]);
export const aiTipPref = createSelector(
  'aiTipPref',
  [(state) => state.ui.prefs?.aiTip, (_, key) => key], //
  (aiTip, key) => aiTip?.[key]
);
export const rawAiHistory = (state) => state.ui.prefs?.aiHistory;
export const aiHistory = createSelector('aiHistory', [rawAiHistory], (history = []) => {
  const filtered = history.filter(Boolean);
  const reversed = [...filtered].reverse();
  const dedupReversed = reversed.filter((item, idx) => reversed.indexOf(item) === idx);
  return dedupReversed.reverse();
});
export const aiChatOpen = (state) => state?.ui?.aiChat?.open;
export const aiChatMessages = (state) => state?.ui?.aiChat?.messages;
export const aiContext = (state) => state?.ui?.aiContext;
export const contextMenu = (state) => state?.ui?.contextMenu;
export const contextMenuTarget = (state) => state?.ui?.contextMenuTarget;
export const modals = (state) => state?.ui?.modals;
export const hasOpenModal = (state) => Object.values(state?.ui?.modals).some((modal) => modal?.open);
export const filter = createSelector(
  'filter',
  [filters, companyId, (_, key, __) => key, (_, __, global = true) => global], //
  (filters, companyId, key, global) => (global ? filters?.[`${key}`] : filters?.[`${companyId}-${key}`])
);
export const websocket = (state) => state?.ui?.websocket;
export const errorSplashScreen = (state) => state?.ui?.errorSplashScreen;
export const loadStates = (state) => state?.ui?.loadState || {};
export const isLoading = createSelector(
  'isLoading',
  [(state) => state.ui.loadState, (_, key) => key], //
  (obj, key) => obj?.[key]?.isLoading
);
export const loadState = createSelector(
  'loadState',
  [(state) => state.ui.loadState, (_, key) => key], //
  (obj, key) => obj?.[key]
);
export const externalUpdate = createSelector(
  'externalUpdate',
  [(state) => state.ui.externalUpdates, (_, key) => key], //
  (obj, key) => obj?.find((n) => n.key === key)?.timestamp
);
export const signupState = (state) => state?.ui?.signupState;
export const signupCurrentStep = (state) => state?.ui?.signupState?.currentStep;
export const signupEmailAddress = (state) => state?.ui?.signupState?.emailAddress;
export const signupAttempt = (state) => state?.ui?.signupState?.signupAttempt;
export const signupFlowStarted = (state) => state?.ui?.signupState?.flowStarted;
export const signupTimestamp = (state) => state?.ui?.signupState?.timestamp;
export const signupIsOAuth = (state) => state?.ui?.signupState?.isOAuth;
export const signupIsComplete = (state) => state?.ui?.signupState?.isComplete;
export const signupIsEmailLocked = (state) => state?.ui?.signupState?.isEmailLocked;

export const modal = createSelector(
  'modal',
  [modals, (_, key) => key], //
  (modals, key) => modals?.[key]
);

export const modalStack = (state) => state?.ui?.modalStack || [];

export const modalStackPosition = createSelector('modalStackPosition', [modalStack, (_, modalId) => modalId], (stack, modalId) => {
  const position = stack.indexOf(modalId);
  return position;
});

export const controls = (state) => state?.ui?.controls;
export const control = createSelector(
  'control',
  [controls, (_, key) => key], //
  (controls, key) => controls?.[key]
);
